import { Box, Typography, Chip, IconButton, useTheme } from "@mui/material";
import {
  ImageOutlined,
  LocationOnOutlined,
  Inventory2Outlined,
  SellOutlined,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import type { IBrandModel, IEquipment } from "../../../types/equipment";
import Button from "../../../components/ui/Button";
import PenPaperIcon from "../../../components/icons/PenPaperIcon";

interface Props {
  equipment: IEquipment;
  onImageSearch: () => void;
  onEdit: () => void;
  onReportIssue: () => void;
  onRentOut: () => void;
  brandModel?: IBrandModel | null;
  onScheduleMaintenance: () => void;
}

export default function GearHeader({
  equipment,
  onImageSearch,
  onEdit,
  onReportIssue,
  onRentOut,
  brandModel,
  onScheduleMaintenance,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        backgroundColor: colors.primary[900],
        borderRadius: "16px",
        p: 3,
        mb: 3,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 4,
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              position: "relative",
              width: 150,
              height: 150,
              borderRadius: "12px",
              backgroundColor: colors.primary[800],
              display: "grid",
              placeItems: "center",
              color: colors.primary[900],
              overflow: "hidden",
              border: `2px solid ${colors.primary[600]}`,
            }}
          >
            {equipment.images && equipment.images.length > 0 ? (
              <img
                src={equipment.images[0].image || equipment.images[0].image}
                alt={equipment.gear_name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <ImageOutlined sx={{ fontSize: 40 }} />
            )}

            <Button
              variant="contained"
              onClick={onImageSearch}
              size="small"
              sx={{
                position: "absolute",
                bottom: 8,
                left: "50%",
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
                backgroundColor: colors.primary[900],
                color: theme.palette.text.secondary,
                textTransform: "none",
                fontSize: "0.6rem",
                boxShadow: "none",
                borderRadius: "4px",
                px: 1.5,
                py: 0.2,
              }}
            >
              Search with google
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 150,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label={
                typeof equipment.brand_model?.category === "object"
                  ? equipment.brand_model?.category?.name
                  : equipment.brand_model?.category || "Video"
              }
              size="small"
              sx={{
                bgcolor: colors.primary[800],
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            />
            <Chip
              label={equipment.condition || "Excellent"}
              size="small"
              sx={{
                bgcolor: colors.primary[800],
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            />
            <Chip
              label={equipment.status || "Available"}
              size="small"
              sx={{
                bgcolor: colors.primary[800],
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              color={theme.palette.text.primary}
            >
              {equipment.gear_name}
            </Typography>
            <Typography
              variant="body1"
              color={theme.palette.text.secondary}
              sx={{
                mb: 2,
                textTransform: "uppercase",
                fontSize: "0.85rem",
                letterSpacing: 1,
              }}
            >
              {equipment.brand_model?.brand_name}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
            <Chip
              icon={
                <LocationOnOutlined
                  sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                />
              }
              label={equipment.location || "Gourment Garage, Harare"}
              size="small"
              sx={{
                bgcolor: colors.primary[800],
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Inventory2Outlined
                sx={{ fontSize: 16, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="body2"
                color={theme.palette.text.primary}
                fontWeight={600}
                fontSize="0.8rem"
              >
                {brandModel?.available_quantity} of{" "}
                {brandModel?.brand_model_quantity} In stock
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <SellOutlined
                sx={{ fontSize: 16, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="body2"
                color={theme.palette.text.primary}
                fontWeight={600}
                fontSize="0.8rem"
              >
                {equipment.serial_number}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          alignSelf: "flex-start",
        }}
      >
        <Button
          variant="contained"
          onClick={onScheduleMaintenance}
          sx={{
            height: 34,
            px: 3,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: colors.primary[400],
            color: colors.primary[900],
            "&:hover": { backgroundColor: colors.primary[50] },
          }}
        >
          Schedule Maintenance
        </Button>

        <Button
          variant="contained"
          onClick={onReportIssue}
          sx={{
            height: 34,
            px: 3,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: colors.primary[500],
            color: colors.primary[900],
            "&:hover": { backgroundColor: colors.primary[400] },
          }}
        >
          Report Issue
        </Button>

        <Button
          variant="outlined"
          onClick={onRentOut}
          sx={{
            height: 34,
            px: 3,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            borderColor: colors.primary[400],
            color: theme.palette.text.primary,
            "&:hover": {
              borderColor: colors.primary[500],
              backgroundColor: colors.primary[200],
            },
          }}
        >
          Rent out
        </Button>

        <IconButton
          onClick={onEdit}
          sx={{
            height: 34,
            width: 34,
            borderRadius: "8px",
            backgroundColor: colors.primary[400],
            color: colors.primary[900],
            "&:hover": { backgroundColor: colors.primary[50] },
          }}
        >
          <PenPaperIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
